// src/pages/EmployeeLeave.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { db, storage } from "../firebase/config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  collection,
  getDocs,
  setDoc,
  addDoc,
  getDoc,
  doc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "../styles/EmployeeLeave.css";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { useAuth } from "../Context/AuthContext";

export default function EmployeeLeave() {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypeCode, setLeaveTypeCode] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [timeOfDay, setTimeOfDay] = useState("Full Day");
  const [supportingDoc, setSupportingDoc] = useState(null);

  const [companyId, setCompanyId] = useState(null);
  const [userUid, setUserUid] = useState(null); // ✅ fix: stable auth uid for listeners

  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("request");

  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [timeBankEnabled, setTimeBankEnabled] = useState(false);
  const [timeBank, setTimeBank] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [enabledTypes, setEnabledTypes] = useState([]);

  // custom time for half day
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // 🆕 Leave policy: full-day hours
  const [fullDayHours, setFullDayHours] = useState(8);
  const { user, loading: authLoading } = useAuth();

  // ref to reset file input
  const fileRef = useRef(null);

  // auto-select first enabled leave type
  useEffect(() => {
    if (enabledTypes.length && !leaveTypeCode) {
      setLeaveTypeCode(enabledTypes[0].code);
    }
  }, [enabledTypes, leaveTypeCode]);

  // Utility: format date in company TZ
  const fmt = (iso) =>
    DateTime.fromISO(iso, { zone: companyTimeZone }).toFormat("MMM dd, yyyy");

  // ─────────────────────────────────────────────────────────────
  // Auth + find company + load policy + time bank
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError("");

        // Wait for AuthContext to resolve
        if (authLoading) return;

        if (!user?.uid) {
          setUserUid(null);
          setCompanyId(null);
          setError("User not authenticated.");
          return;
        }

        const uid = user.uid;
        setUserUid(uid);

        // ✅ 1) Get companyId without scanning
        let cid = user.companyId || null;

        // fallback: read users/{uid} once (still NOT scanning)
        if (!cid) {
          const userSnap = await getDoc(doc(db, "users", uid));
          cid = userSnap.exists() ? userSnap.data()?.companyId || null : null;
        }

        if (!cid) throw new Error("Company ID not found.");

        setCompanyId(cid);

        // ✅ 2) Load company timezone + leave policy
        const companyDocSnap = await getDoc(doc(db, "companies", cid));
        const cData = companyDocSnap.exists() ? companyDocSnap.data() : {};
        const companyTZ = cData?.timeZone || "UTC";
        setCompanyTimeZone(companyTZ);

        const policyHours = Number(cData?.leavePolicy?.fullDayHours);
        setFullDayHours(
          Number.isFinite(policyHours) && policyHours > 0 ? policyHours : 8,
        );

        // ✅ 3) Load employee time bank info from employee doc (single doc read)
        const empSnap = await getDoc(
          doc(db, `companies/${cid}/employees`, uid),
        );
        const empData = empSnap.exists() ? empSnap.data() : {};
        setTimeBankEnabled(!!empData.timeBankEnabled);
        setTimeBank(empData.timeBank || {});
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load company info.");
        setCompanyId(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [user?.uid, user?.companyId, authLoading]);

  // ─────────────────────────────────────────────────────────────
  // Load enabled leave types (live)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;

    setLeaveTypesLoading(true);

    const refCol = collection(db, `companies/${companyId}/leave_types`);

    const unsub = onSnapshot(
      refCol,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        setEnabledTypes(
          rows
            .filter((t) => t.enabled !== false)
            .sort((a, b) =>
              String(a.label || "").localeCompare(String(b.label || "")),
            ),
        );

        setLeaveTypesLoading(false);
      },
      () => {
        setLeaveTypesLoading(false);
      },
    );

    return () => unsub();
  }, [companyId]);

  // ─────────────────────────────────────────────────────────────
  // Leave history (live + sorted) ✅ FIXED
  // ─────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────
  // Leave history (live) ✅ NO INDEX REQUIRED
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !userUid) return;

    setHistoryLoading(true);
    setError("");

    const qRef = query(
      collection(db, `companies/${companyId}/leave_requests`),
      where("employeeId", "==", userUid),
      // ❌ remove orderBy(...) to avoid composite index requirement
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const requests = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            // Prefer Firestore timestamp if present
            const aTs = a.createdAtTs?.toMillis
              ? a.createdAtTs.toMillis()
              : null;
            const bTs = b.createdAtTs?.toMillis
              ? b.createdAtTs.toMillis()
              : null;

            if (aTs != null && bTs != null) return bTs - aTs;

            // Fall back to createdAt ISO string
            const aISO = a.createdAt
              ? Date.parse(a.createdAt)
              : Date.parse(a.startDate || "");
            const bISO = b.createdAt
              ? Date.parse(b.createdAt)
              : Date.parse(b.startDate || "");
            return (bISO || 0) - (aISO || 0);
          });

        setLeaveRequests(requests);
        setHistoryLoading(false);
      },
      (err) => {
        console.error("Live leave history error:", err);
        setError(err.message || "Failed to load leave history.");
        setLeaveRequests([]);
        setHistoryLoading(false);
      },
    );

    return () => unsub();
  }, [companyId, userUid]);

  // (optional) manual refresh helper (not required with onSnapshot, but safe)
  const fetchLeaveRequests = useCallback(async () => {
    try {
      if (!companyId || !userUid) return;

      const qRef = query(
        collection(db, `companies/${companyId}/leave_requests`),
        where("employeeId", "==", userUid),
      );

      const snapshot = await getDocs(qRef);
      const requests = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort(
          (a, b) =>
            new Date(b.createdAt || b.startDate) -
            new Date(a.createdAt || a.startDate),
        );

      setLeaveRequests(requests);
    } catch (err) {
      console.error("Error loading leave requests:", err.message);
    }
  }, [companyId, userUid]);

  // ─────────────────────────────────────────────────────────────
  // Helpers for requested hours
  // ─────────────────────────────────────────────────────────────
  const calculateBusinessDays = (start, end) => {
    if (!start || !end) return 0;

    let d1 = DateTime.fromISO(start, { zone: companyTimeZone });
    let d2 = DateTime.fromISO(end, { zone: companyTimeZone });
    if (d2 < d1) [d1, d2] = [d2, d1]; // order safety

    let days = 0;
    for (let d = d1; d <= d2; d = d.plus({ days: 1 })) {
      days++; // count every day, including weekend
    }
    return days;
  };

  const todayISO = DateTime.now()
    .setZone(companyTimeZone)
    .toISODate();
  const endMinISO = startDate
    ? DateTime.fromISO(startDate, { zone: companyTimeZone }).toISODate()
    : todayISO;

  const requestedDays =
    startDate && endDate ? calculateBusinessDays(startDate, endDate) : 0;

  let hoursPerDay = 0;

  if (timeOfDay === "Full Day") {
    hoursPerDay = Number(fullDayHours || 8);
  } else if (timeOfDay === "Half Day") {
    if (customStart && customEnd) {
      const baseDate = "2000-01-01";
      const startDT = DateTime.fromISO(`${baseDate}T${customStart}`);
      const endDT = DateTime.fromISO(`${baseDate}T${customEnd}`);
      const diff = endDT.diff(startDT, "hours").hours;
      hoursPerDay = diff > 0 ? diff : 0;
    } else {
      hoursPerDay = 4;
    }
  }

  const requestedHours =
    requestedDays > 0 && hoursPerDay > 0 ? requestedDays * hoursPerDay : 0;

  // ─────────────────────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      setError("");

      if (!companyId) throw new Error("Company ID not found.");

      if (!user) throw new Error("User not authenticated.");

      // prevent overlapping leave requests (blocks Pending/Approved)
      const newStartDT = DateTime.fromISO(startDate, { zone: companyTimeZone });
      const newEndDT = DateTime.fromISO(endDate, { zone: companyTimeZone });

      const hasOverlap = leaveRequests.some((req) => {
        if (!req.startDate || !req.endDate) return false;
        if (req.status === "Rejected" || req.status === "Cancelled")
          return false;

        const existingStart = DateTime.fromISO(req.startDate, {
          zone: companyTimeZone,
        });
        const existingEnd = DateTime.fromISO(req.endDate, {
          zone: companyTimeZone,
        });

        return existingStart <= newEndDT && existingEnd >= newStartDT;
      });

      if (hasOverlap) {
        setError(
          "You already have a leave request that covers one or more of these dates. Please choose a different date range or wait for the existing request to be processed.",
        );
        setSubmitting(false);
        return;
      }

      // employee name
      const employeeRef = doc(db, `companies/${companyId}/employees`, user.uid);
      const employeeSnap = await getDoc(employeeRef);
      const employeeData = employeeSnap.exists() ? employeeSnap.data() : null;

      const employeeName =
        employeeData?.preferredFirstName ||
        employeeData?.name ||
        `${employeeData?.firstName || ""} ${employeeData?.lastName ||
          ""}`.trim() ||
        "Unknown";

      // selected leave type
      const selectedType = enabledTypes.find((t) => t.code === leaveTypeCode);
      if (!selectedType) throw new Error("Leave type not found.");

      const leaveTypeLabel = selectedType.label;

      // time bank guard
      if (timeBankEnabled) {
        const available = Number(timeBank?.[leaveTypeCode] || 0);
        if (requestedHours > available) {
          setError(
            `❌ Insufficient ${leaveTypeLabel} hours. You requested ${requestedHours}h but only have ${available}h available.`,
          );
          setSubmitting(false);
          return;
        }
      }

      // upload optional doc
      let supportingDocUrl = "";
      if (supportingDoc) {
        const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        const maxSizeBytes = 5 * 1024 * 1024;

        if (!allowedTypes.includes(supportingDoc.type)) {
          setError(
            "File type not allowed. Please upload a JPG, PNG, or PDF file.",
          );
          setSubmitting(false);
          return;
        }

        if (supportingDoc.size > maxSizeBytes) {
          setError("File is too large. Maximum file size is 5MB.");
          setSubmitting(false);
          return;
        }

        const fileRefStorage = ref(
          storage,
          `supporting_documents/${user.uid}_${Date.now()}_${
            supportingDoc.name
          }`,
        );

        await uploadBytes(fileRefStorage, supportingDoc);
        supportingDocUrl = await getDownloadURL(fileRefStorage);
      }

      // Create request
      const leaveRef = await addDoc(
        collection(db, `companies/${companyId}/leave_requests`),
        {
          employeeId: user.uid,
          employeeName,
          leaveTypeCode,
          leaveTypeLabel,
          startDate,
          endDate,
          reason: (reason || "").trim(),
          timeOfDay,
          supportingDocUrl,
          customStart: timeOfDay === "Half Day" ? customStart : "",
          customEnd: timeOfDay === "Half Day" ? customEnd : "",
          status: "Pending",
          createdAt: DateTime.now()
            .setZone(companyTimeZone)
            .toISO(),
          createdAtTs: serverTimestamp(), // ✅ recommended for sorting
        },
      );

      toast.success("✅ Leave request submitted!");
      // ✅ OPTIONAL: take them to the history tab immediately
      setActiveTab("history");

      // Notify company
      await setDoc(
        doc(db, `companies/${companyId}/notifications`, leaveRef.id),
        {
          employeeId: user.uid,
          employeeName,
          status: "Pending",
          type: "leaveRequest",
          timestamp: DateTime.now()
            .setZone(companyTimeZone)
            .toISO(),
          read: false,
          message: `Leave request submitted by ${employeeName} for ${leaveTypeLabel} from ${startDate} to ${endDate} (${timeOfDay}${
            timeOfDay === "Half Day" && customStart && customEnd
              ? `, ${customStart}–${customEnd}`
              : ""
          }).`,
        },
      );

      // optional refresh (history is live anyway)
      await fetchLeaveRequests();

      // Reset fields
      setLeaveTypeCode(enabledTypes[0]?.code || "");
      setStartDate("");
      setEndDate("");
      setReason("");
      setTimeOfDay("Full Day");
      setSupportingDoc(null);
      setCustomStart("");
      setCustomEnd("");
      setError("");

      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      console.error("Error submitting leave request:", err.message);
      setError(err.message || "Failed to submit leave request.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTimeOfDayChange = (value) => {
    setTimeOfDay(value);
    if (value === "Full Day") {
      setCustomStart("");
      setCustomEnd("");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="employeeleave-container">
      <ToastContainer position="top-right" autoClose={2500} />
      <nav className="employeeleave-nav">
        <p className="employeeleave-breadcrumb">
          <Link to="/employee-dashboard">Dashboard</Link> /{" "}
          <span>Leave request</span>
        </p>
      </nav>

      <header className="el-header">
        <div>
          <h1 className="el-title">Leave request</h1>
          <div className="el-sub">Submit a new request or review history.</div>
        </div>
      </header>

      <div className="employeeleave-tabs">
        <button
          className={`employeeleave-tab ${
            activeTab === "request" ? "active" : ""
          }`}
          onClick={() => setActiveTab("request")}
          type="button"
        >
          New Leave Request
        </button>

        <button
          className={`employeeleave-tab ${
            activeTab === "history" ? "active" : ""
          }`}
          onClick={() => setActiveTab("history")}
          type="button"
        >
          Previous Requests
        </button>
      </div>

      {activeTab === "request" && (
        <section className="el-card">
          {loading ? (
            <div className="el-hint">Loading…</div>
          ) : error ? (
            <div className="el-hint" style={{ color: "#b91c1c" }}>
              {error}
            </div>
          ) : null}

          {leaveTypesLoading ? (
            <div className="el-hint">Loading leave types…</div>
          ) : enabledTypes.length === 0 ? (
            <div className="el-card" style={{ padding: 16 }}>
              <h3 className="el-title" style={{ marginBottom: 8 }}>
                No leave types available
              </h3>
              <div className="el-hint" style={{ color: "#b91c1c" }}>
                Your employer hasn’t set up any leave types yet.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="el-row">
              {/* Leave type */}
              <div>
                <label className="el-label">Leave Type</label>
                <select
                  className="el-select"
                  value={leaveTypeCode}
                  onChange={(e) => setLeaveTypeCode(e.target.value)}
                >
                  {enabledTypes.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.label}
                    </option>
                  ))}
                </select>

                {timeBankEnabled && leaveTypeCode && (
                  <div className="el-hint">
                    Balance:{" "}
                    <strong>{Number(timeBank?.[leaveTypeCode] || 0)}h</strong>
                  </div>
                )}
              </div>

              {/* Time of day */}
              <div>
                <label className="el-label">Time of Day</label>
                <select
                  className="el-select"
                  value={timeOfDay}
                  onChange={(e) => handleTimeOfDayChange(e.target.value)}
                >
                  <option value="Full Day">Full Day</option>
                  <option value="Half Day">Half Day</option>
                </select>
              </div>

              {/* Start */}
              <div>
                <label className="el-label">Start Date</label>
                <input
                  className="el-input"
                  type="date"
                  min={todayISO}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>

              {/* End */}
              <div>
                <label className="el-label">End Date</label>
                <input
                  className="el-input"
                  type="date"
                  min={endMinISO}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>

              {/* Half Day custom times */}
              {timeOfDay === "Half Day" && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    gap: "20px",
                  }}
                >
                  <div>
                    <label className="el-label">Custom Start Time</label>
                    <input
                      className="el-input"
                      type="time"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className="el-label">Custom End Time</label>
                    <input
                      className="el-input"
                      type="time"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Reason */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label className="el-label">Reason</label>
                <textarea
                  className="el-textarea"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={300}
                />
                <div className="el-hint">{reason.length}/300 characters</div>
              </div>

              {/* Upload */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label className="el-label">
                  Upload Supporting Document (optional)
                </label>
                <input
                  ref={fileRef}
                  className="el-file"
                  type="file"
                  onChange={(e) =>
                    setSupportingDoc(e.target.files?.[0] || null)
                  }
                  accept=".jpg,.jpeg,.png,.pdf"
                />
                {supportingDoc && (
                  <div className="el-hint">Selected: {supportingDoc.name}</div>
                )}
              </div>

              {/* Summary */}
              <div style={{ gridColumn: "1 / -1" }} className="el-inline">
                <span className="pill">
                  ⏱ {requestedDays || 0} business day
                  {requestedDays === 1 ? "" : "s"}
                </span>

                <span className="pill">{requestedHours || 0} hours</span>

                {startDate && endDate && (
                  <span className="el-hint">
                    {fmt(startDate)} – {fmt(endDate)} ({timeOfDay}
                    {timeOfDay === "Half Day" &&
                    customStart &&
                    customEnd &&
                    requestedHours
                      ? ` · ${customStart}–${customEnd}`
                      : ""}
                    )
                  </span>
                )}
              </div>

              {/* Submit */}
              <div style={{ gridColumn: "1 / -1" }}>
                <button
                  type="submit"
                  className="btn btn-block"
                  disabled={!companyId || submitting}
                >
                  {submitting ? "Submitting…" : "Submit"}
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      {activeTab === "history" && (
        <section className="el-card">
          <h3 className="el-title" style={{ fontSize: 18, marginBottom: 10 }}>
            Previous Leave Requests
          </h3>

          {historyLoading ? (
            <div className="el-hint">Loading…</div>
          ) : leaveRequests.length === 0 ? (
            <div className="el-empty">No leave requests submitted yet.</div>
          ) : (
            <div className="el-list">
              {leaveRequests.map((req) => (
                <div key={req.id} className="el-item">
                  <div
                    className="el-inline"
                    style={{ justifyContent: "space-between" }}
                  >
                    <h4>
                      {req.leaveTypeLabel || req.leaveType}
                      {req.timeOfDay && req.timeOfDay !== "Full Day" && (
                        <span className="pill" style={{ marginLeft: 6 }}>
                          {req.timeOfDay}
                        </span>
                      )}
                    </h4>

                    <span
                      className="pill"
                      style={{ color: getStatusColor(req.status) }}
                    >
                      {req.status}
                    </span>
                  </div>

                  <div className="el-meta">
                    {fmt(req.startDate)} – {fmt(req.endDate)}
                  </div>

                  {req.supportingDocUrl && (
                    <a
                      className="el-hint"
                      href={req.supportingDocUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View attachment ↗
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case "Pending":
      return "#d97706";
    case "Approved":
      return "#16a34a";
    case "Rejected":
      return "#dc2626";
    case "Cancelled":
      return "#64748b";
    default:
      return "#000";
  }
}
